import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TableContext } from '../../contexts/tableContext';
import { getCookies } from '../../hooks/useCookies';
import envValues from '../../enviornment';
import styles from './MultipleFileUpload.module.scss';

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/heic',
  'image/heif',
];

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'heic', 'heif'];

const isImageFile = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  if (file.type && IMAGE_MIME_TYPES.includes(file.type.toLowerCase())) {
    return true;
  }
  return IMAGE_EXTENSIONS.includes(extension);
};

const buildPreviewItems = (fileList, imagesOnly = false) => {
  const previews = [];
  for (let i = 0; i < fileList.length; i += 1) {
    const file = fileList[i];
    const extension = file.name.split('.').pop()?.toLowerCase();
    let type = 'image';
    if (!imagesOnly) {
      if (extension === 'mp4' || extension === 'mov') {
        type = 'video';
      } else if (extension === 'pdf') {
        type = 'pdf';
      }
    }
    previews.push({ imgurl: URL.createObjectURL(file), extension: type });
  }
  return previews;
};

function MultipleFileUpload({ editFiles, section, imagesOnly }) {
  const [edited, setEdited] = useState(editFiles);
  const [uploadedImgs, setUplodedImgs] = useState([]);
  const { files, setFiles } = useContext(TableContext);
  const fileInputRef = useRef(null);
  const token = `Bearer ${getCookies('Token')}`;
  const params = useParams();
  const { userId } = params;
  const pdfImg =
    'https://thesoftwarepro.com/wp-content/uploads/2019/12/microsoft-office-pdf-document-953x1024.jpg';
  const videoImg =
    'https://image.shutterstock.com/image-vector/play-button-icon-vector-illustration-260nw-1697833306.jpg';

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!files || files.length === 0) {
      setUplodedImgs([]);
      clearFileInput();
    }
  }, [files, clearFileInput]);

  const handleChange = (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }
    const fileArray = Array.from(selectedFiles);

    if (imagesOnly) {
      const invalidFiles = fileArray.filter((file) => !isImageFile(file));
      if (invalidFiles.length > 0) {
        toast.error('Only image files are allowed (JPG, PNG, GIF, WebP, etc.)');
        clearFileInput();
        return;
      }
    }

    setUplodedImgs(buildPreviewItems(selectedFiles, imagesOnly));
    setFiles(fileArray);
  };

  const handleCancelButton = (param) => () => {
    const remainingPreviews = uploadedImgs.filter((_, index) => index !== param);
    const remainingFiles = Array.isArray(files)
      ? files.filter((_, index) => index !== param)
      : [];
    setUplodedImgs(remainingPreviews);
    setFiles(remainingFiles);
    clearFileInput();
  };

  const handleRemoveButton = (param) => () => {
    axios
      .delete(`${envValues.REACT_APP_API_ENDPOINT}/${section}/${userId}/${param}`, {
        headers: { Authorization: token },
      })
      .then((res) => {
        setEdited(res.data.data.user.files);
        toast.success('File removed!');
      });
  };
  return (
    <div className="form-group">
      <div className="d-flex">
        <div className="d-flex">
          <div className="file-uploader-mask d-flex justify-content-center align-items-center">
            {/* <img
              className="file-uploader-icon"
              src="https://pic.onlinewebfonts.com/svg/img_150954.png"
              style={{ height: 20, width: 20 }}
              alt="Upload-Icon"
            /> */}
          </div>
          <input
            ref={fileInputRef}
            multiple
            className="file-input"
            id="upload"
            type="file"
            accept={imagesOnly ? 'image/*' : undefined}
            onChange={handleChange}
            hidden
          />
          <label htmlFor="upload" className={styles.upload_btn}>
            Choose file
          </label>
        </div>
      </div>
      {imagesOnly && (
        <p className={styles.hintText}>Only image files (JPG, PNG, GIF, WebP) are allowed.</p>
      )}
      <div className="d-flex flex-wrap mt-4">
        {edited != null
          ? edited.map((uploadedImg, index) => (
              <div className={styles.close_icon_main}>
                <img
                  src={uploadedImg.path}
                  key={uploadedImg.path}
                  style={{ height: 100, width: 100, objectFit: 'contain' }}
                  alt="UploadedImages"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = uploadedImg.extension === 'video' ? videoImg : pdfImg;
                  }}
                  className="img-thumbnail img-fluid uploaded-img mr-5"
                />
                <button
                  type="button"
                  className={styles.close_icon}
                  onClick={handleRemoveButton(index)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 19 19"
                    stroke="#60677e"
                    strokeWidth="2"
                    className="react-date-picker__clear-button__icon react-date-picker__button__icon"
                    style={{ marginTop: '-10px', marginLeft: '-3px' }}
                  >
                    <line x1="4" x2="15" y1="4" y2="15" />
                    <line x1="15" x2="4" y1="4" y2="15" />
                  </svg>
                </button>
              </div>
            ))
          : null}
      </div>
      <div className="image upload">
        {uploadedImgs != null
          ? uploadedImgs.map((uploadedImg, index) => (
              <>
                <div className={styles.uploaded_imgsection}>
                  <img
                    src={uploadedImg.imgurl}
                    key={uploadedImg.imgurl}
                    alt="UploadedImages"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = uploadedImg.extension === 'video' ? videoImg : pdfImg;
                    }}
                    className="img-thumbnail img-fluid uploaded-img mr-2"
                    id={styles.uploaded_img}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleCancelButton(index)}
                  className={styles.imgcancel_btn}
                >
                  Cancel
                </button>
              </>
            ))
          : null}
      </div>
    </div>
  );
}
MultipleFileUpload.propTypes = {
  editFiles: PropTypes.element,
  section: PropTypes.element,
  imagesOnly: PropTypes.bool,
};
MultipleFileUpload.defaultProps = {
  editFiles: null,
  section: 'user',
  imagesOnly: false,
};

export default MultipleFileUpload;
